/*
  OK FBI — Phase II: Standalone OBS-safe Investigator Card Wall route.

  Route: /broadcast/card-wall?owner_user_id=<uid>&count=<optional preview count>
  Purpose: A single-purpose OBS Browser Source page that renders the live
  investigator card wall for a specific streamer.

  Two modes:
   • `owner_user_id` param present → read real lobby via /api/session/lobby
     (only works for streamers looking at their own lobby; if unauthenticated
     the page shows an empty state — fine for previewing without login).
   • `count` param present → render N simulated participants for
     visual verification (used by OWNER/production before real joins).

  Everything inside the wall stays within the 16:9 safe area — no scroll,
  no overflow, no manual pagination.
*/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { InvestigatorCardWall } from "@/components/InvestigatorCardWall";
import { SiteTicker } from "@/pages/AnnouncementsPage";

const POLL_MS = 2500;

function buildSampleParticipants(count, cats) {
  if (!cats.length || !count) return [];
  const names = ["أحمد","ليلى","سارة","خالد","نور","محمد","هدى","علي","ريم","ياسر",
                 "فاطمة","عمر","سلمى","حسن","دانا","تركي","مي","مروان","لينا","فارس",
                 "بدر","نجلاء","سيف","رهف"];
  const ranks = ["rookie","detective","senior","master"];
  // Palette used for the fallback avatars — cycles per index so cards
  // are visually distinct without needing external image assets.
  const palette = ["#7c3aed","#0891b2","#059669","#d97706","#dc2626","#db2777","#4f46e5","#f59e0b"];
  const svgAvatar = (initial, color) => {
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 96 96'><rect width='96' height='96' rx='48' fill='${color}'/><text x='50%' y='58%' font-family='Arial,sans-serif' font-size='48' font-weight='800' text-anchor='middle' fill='white'>${initial}</text></svg>`;
    return "data:image/svg+xml;utf8," + encodeURIComponent(svg);
  };
  const badgePool = {
    standard: ["أول قضية", "شاهد أمين"],
    streamer: ["مذيع معتمد", "بث مباشر", "٥ بثوث"],
    vip:      ["VIP", "شهرين متتاليين", "منقذ قضية", "جواب سريع"],
    special_3:["معرّف خاص", "لقب نادر", "أعلى نقاط", "أفضل تحقيق"],
    elite_2:  ["نخبة", "قاهر القضية", "١٠ إجابات", "٣ أشهر", "معرّف نادر"],
    royal_1:  ["ROYAL", "مؤسس", "أعلى المستويات", "لقب ملكي", "١٠ قضايا", "متابع مدى الحياة"],
  };
  const out = [];
  for (let i = 0; i < count; i += 1) {
    const cat = cats[i % cats.length];
    const pool = badgePool[cat] || [];
    const take = Math.min(pool.length, 2 + (i % (pool.length + 1 || 1)));
    const name = `${names[i % names.length]} ${String(i + 1).padStart(2, "0")}`;
    out.push({
      id: `sim-${i}`,
      display_name: name,
      tiktok_username: `agent_${(i + 1).toString().padStart(3, "0")}`,
      // Slice 1 refinement: every sample carries a TikTok-style avatar so
      // visual verification can prove the avatar zone renders on the compact
      // Live Wall at every density. Real TikTok integration later swaps
      // this URL for the participant's actual profile photo.
      avatar_url: svgAvatar(name.slice(0, 1), palette[i % palette.length]),
      agent_id: cat === "royal_1" ? String((i % 9) + 1)
                : cat === "elite_2" ? String(((i % 9) + 1) * 11)
                : cat === "special_3" ? String(((i % 9) + 1) * 111)
                : String(4000 + i),
      rank: ranks[i % ranks.length],
      points: (i + 1) * 137,
      card_category: cat,
      badges: pool.slice(0, take),
      streak: (i % 5 === 0) ? { months: 3 + (i % 6) } : null,
    });
  }
  return out;
}


export default function BroadcastCardWallPage() {
  const [params] = useSearchParams();
  const previewCount = parseInt(params.get("count") || "0", 10) || 0;
  // NOTE: owner_user_id support is currently a stub — the auth cookie on
  // the OBS browser source is the streamer's session, and /api/session/lobby
  // already routes by that. This param is reserved for the future public
  // OBS URL wiring after TikTok production integration.

  const [templatesByCategory, setTemplatesByCategory] = useState({});
  const [participants, setParticipants] = useState([]);

  const loadTemplates = useCallback(async () => {
    try {
      const { data } = await api.get("/cards/templates");
      const m = {};
      for (const t of data || []) m[t.category] = t;
      setTemplatesByCategory(m);
    } catch { /* keep last */ }
  }, []);

  const loadLobby = useCallback(async () => {
    if (previewCount) return;   // preview mode ignores the real lobby
    try {
      const { data } = await api.get("/session/lobby");
      setParticipants(data?.participants || []);
    } catch {
      // Unauthenticated / not a streamer → empty wall.
      setParticipants([]);
    }
  }, [previewCount]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  useEffect(() => {
    document.title = "OK FBI · Card Wall";
    if (previewCount) return;
    loadLobby();
    const id = setInterval(loadLobby, POLL_MS);
    return () => clearInterval(id);
  }, [loadLobby, previewCount]);

  const shown = useMemo(() => {
    if (previewCount) {
      const cats = Object.keys(templatesByCategory);
      if (!cats.length) return [];
      return buildSampleParticipants(previewCount, cats);
    }
    return participants;
  }, [previewCount, participants, templatesByCategory]);

  return (
    <div className="w-screen h-screen bg-transparent overflow-hidden flex flex-col" dir="rtl"
         data-testid="broadcast-card-wall">
      <SiteTicker scope="broadcast" />
      <div className="w-full flex-1 p-3">
        <InvestigatorCardWall
          participants={shown}
          templatesByCategory={templatesByCategory}/>
      </div>
    </div>
  );
}
